import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Check, Search, Tv, MessageSquare, Stethoscope, Home, Building2, Briefcase } from 'lucide-react';

const shiftCards = [
  { label: 'AI Search', icon: <MessageSquare className="w-6 h-6" />, query: '"best dentist near me"', kind: 'query' },
  { label: 'Streaming TV', icon: <Tv className="w-6 h-6" />, query: 'HVAC ad mid-show on Hulu', kind: 'tv' },
  { label: 'Traditional Search', icon: <Search className="w-6 h-6" />, query: '"top accountant small business"', kind: 'query' }
];

const services = [
  {
    name: 'Marqi Rank',
    accent: 'var(--rank-accent)',
    icon: <Search className="w-10 h-10" />,
    tagline: 'Make your clients the answer when buyers ask ChatGPT, Gemini, or Perplexity.',
    features: [
      'Generative Engine Optimisation (GEO)',
      'Answer Engine Optimisation (AEO)',
      'ChatGPT Ads management',
      'AI citation tracking & monthly Share of Voice report'
    ],
    cta: 'Explore MarqiRank',
    href: '/rank'
  },
  {
    name: 'Marqi Cast',
    accent: 'var(--accent-primary)',
    icon: <Tv className="w-10 h-10" />,
    tagline: 'Put your clients on premium streaming TV before their competitors even know it\'s accessible.',
    features: [
      'AI-generated broadcast-ready creative',
      'Disney+, Hulu, Peacock and more',
      'Hyperlocal targeting to 1-mile radius',
      'Foot traffic & website attribution'
    ],
    cta: 'Discover MarqiCast',
    href: '/cast'
  }
];

const verticals = [
  { icon: <Stethoscope className="w-6 h-6" />, name: 'Dental & Med Spa', gap: 'High-LTV, local decisions. Patients now ask AI before they Google — and TV makes a single-location practice look like the obvious choice.' },
  { icon: <Home className="w-6 h-6" />, name: 'Home Services', gap: 'HVAC, roofing, pest control. Seasonal urgency plus zero brand recognition — streaming TV fixes the recognition gap fast.' },
  { icon: <Building2 className="w-6 h-6" />, name: 'Real Estate & Auto', gap: 'Already comfortable buying TV. The gap is AI search — buyers now shortlist agents and dealers before ever calling.' },
  { icon: <Briefcase className="w-6 h-6" />, name: 'Professional Services', gap: 'Accountants, advisers, law firms. High-trust decisions live entirely in AI search results now. Being cited is the new being recommended.' }
];

const queryChips = [
  { text: 'best dentist near me', top: '18%', left: '8%' },
  { text: 'top HVAC company', top: '30%', right: '10%' },
  { text: 'best accountant for small business', bottom: '24%', left: '12%' },
  { text: 'law firm near me', bottom: '18%', right: '14%' },
  { text: 'who fixes roofs in my area', top: '60%', left: '6%' }
];

const Homepage = () => {
  return (
    <>
      <Helmet>
        <title>Marqi — The Discoverability Partner for SMB Agencies</title>
        <meta name="description" content="Discovery has split across AI search, streaming TV, and traditional search. Marqi is the white-label discoverability partner that gets your agency's clients found and chosen across all of them — AI search visibility (Rank) and CTV advertising (Cast), under your brand." />
        <meta name="keywords" content="marqi, discoverability partner for agencies, ai search visibility, ctv for agencies, generative engine optimisation, answer engine optimisation, chatgpt ads, streaming tv advertising, white-label agency services, marqi rank, marqi cast" />
        <link rel="canonical" href="https://marqi.io" />
        <meta property="og:title" content="Marqi — Get Your Clients Found Across AI Search & Streaming TV" />
        <meta property="og:description" content="Your clients' next customer is searching AI — or watching TV. Usually both. Marqi is the white-label partner that gets SMB agencies' clients found in AI search and seen on streaming TV." />
      </Helmet>

      <div className="min-h-screen">
        {/* SECTION 1 — HERO */}
        <section className="relative py-24 lg:py-36 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a0a] via-[#1a1a1a] to-[#0f0f0f]">
            <div className="absolute inset-0 opacity-50">
              <div className="absolute inset-0" style={{
                backgroundImage: `linear-gradient(rgba(218, 255, 1, 0.22) 1px, transparent 1px), linear-gradient(90deg, rgba(218, 255, 1, 0.22) 1px, transparent 1px)`,
                backgroundSize: '34px 34px',
                animation: 'gridSlide 14s linear infinite'
              }}></div>
            </div>
            <div className="absolute -top-20 -right-20 w-[500px] h-[500px] rounded-full bg-gradient-to-r from-[var(--accent-primary)] to-purple-500 opacity-25 blur-3xl" style={{ animation: 'orbFloat 12s ease-in-out infinite' }}></div>
            <div className="absolute -bottom-32 -left-32 w-[600px] h-[600px] rounded-full bg-gradient-to-r from-purple-500 to-[var(--accent-primary)] opacity-20 blur-2xl" style={{ animation: 'orbFloat 16s ease-in-out infinite', animationDelay: '2s' }}></div>

            {/* Floating query chips — reinforce the discoverability theme */}
            <div className="absolute inset-0 hidden md:block">
              {queryChips.map((c, i) => (
                <div
                  key={c.text}
                  className="absolute px-4 py-2 rounded-full border border-[var(--border-subtle)] bg-[rgba(255,255,255,0.03)] text-[var(--text-muted)] text-sm whitespace-nowrap"
                  style={{
                    top: c.top, left: c.left, right: c.right, bottom: c.bottom,
                    opacity: 0.35,
                    animation: `floatUpDown ${5 + i}s ease-in-out infinite`,
                    animationDelay: `${i * 0.6}s`
                  }}
                >
                  <Search className="w-3.5 h-3.5 inline mr-2 -mt-0.5 text-[var(--accent-primary)]" />
                  {c.text}
                </div>
              ))}
            </div>
          </div>

          <div className="container relative z-10">
            <div className="max-w-4xl mx-auto text-center animate-fade-in-up">
              <h1 className="display-lg text-[var(--text-primary)] mb-6" data-testid="hero-title">
                Your Clients’ Next Customer<br />
                Is Searching <span className="text-[var(--rank-accent)]">AI</span> or <span className="whitespace-nowrap">Watching <span className="text-[var(--accent-primary)]">TV</span>.</span><br />
                Usually Both.
              </h1>
              <p className="body-lg mb-12 max-w-2xl mx-auto">
                Some buyers ask ChatGPT or Gemini for a recommendation; others see a brand on streaming TV
                long before they ever search. Marqi gets your agency’s clients found in AI search and seen on
                streaming TV — fully white-labeled, without you hiring a single person.
              </p>

              <div className="flex flex-col sm:flex-row gap-6 justify-center animate-fade-in-scale" style={{ animationDelay: '0.3s' }}>
                <Link to="/contact" className="btn-primary glow-effect" data-testid="hero-cta-primary">
                  Book a Strategy Session
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
                <a href="#the-shift" className="btn-secondary" data-testid="hero-cta-secondary">See How It Works</a>
              </div>

              <p className="text-sm text-[var(--text-muted)] mt-10 tracking-wide">
                Trusted by agencies across the US, UK, Australia and Canada.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 2 — THE SHIFT */}
        <section id="the-shift" className="py-24 bg-[var(--bg-secondary)] scroll-mt-20">
          <div className="container">
            <div className="text-center mb-16 max-w-3xl mx-auto">
              <h2 className="display-md text-[var(--text-primary)] mb-6">Discovery Used to Mean One Thing. Now It Means Three.</h2>
              <p className="body-lg">
                Five years ago, “getting found” meant ranking on Google. Today, the same customer might ask an
                AI assistant, see a brand on streaming TV before they ever search, or do both before making a decision.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {shiftCards.map((c, i) => (
                <div key={c.label} className="feature-card animate-fade-in" style={{ animationDelay: `${i * 0.12}s` }}>
                  <div className="flex items-center mb-6">
                    <div className="w-11 h-11 rounded-full bg-[var(--accent-bg)] flex items-center justify-center text-[var(--accent-primary)] mr-3">{c.icon}</div>
                    <span className="text-sm font-semibold text-[var(--text-muted)] uppercase tracking-wider">{c.label}</span>
                  </div>
                  {c.kind === 'query' ? (
                    <div className="flex items-center px-4 py-3 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-subtle)]">
                      <Search className="w-4 h-4 text-[var(--text-muted)] mr-3 flex-shrink-0" />
                      <span className="text-[var(--text-primary)]">{c.query}</span>
                    </div>
                  ) : (
                    <div className="flex items-center px-4 py-3 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-subtle)]">
                      <Tv className="w-4 h-4 text-[var(--accent-primary)] mr-3 flex-shrink-0" />
                      <span className="text-[var(--text-primary)]">{c.query}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <p className="text-center body-lg mt-12 max-w-2xl mx-auto text-[var(--text-secondary)]">
              Most agencies are still built for the third one. Marqi covers all three — through two services
              your agency can offer today.
            </p>
          </div>
        </section>

        {/* SECTION 3 — RANK + CAST */}
        <section id="services" className="py-24 scroll-mt-20">
          <div className="container">
            <div className="text-center mb-16 max-w-3xl mx-auto">
              <h2 className="display-md text-[var(--text-primary)] mb-6">Found in AI Search. Seen on Streaming TV.</h2>
              <p className="body-lg">
                Marqi Rank gets your clients found in AI search. Marqi Cast gets them seen on streaming TV before
                anyone searches at all. Run one or both — every engagement is white-labeled, fully managed, under
                your agency’s brand.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {services.map((service, index) => (
                <div key={service.name} className="feature-card hover-lift animate-fade-in flex flex-col" style={{ animationDelay: `${index * 0.15}s` }} data-testid={`service-card-${index}`}>
                  <div className="mb-6" style={{ color: service.accent }}>{service.icon}</div>
                  <h3 className="h1 mb-4" style={{ color: service.accent }}>{service.name}</h3>
                  <p className="body-lg mb-8">{service.tagline}</p>

                  <ul className="space-y-3 mb-8 flex-1">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-start text-[var(--text-secondary)]">
                        <Check className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" style={{ color: service.accent }} />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Link to={service.href} className="btn-secondary w-full" data-testid={`service-cta-${index}`}>
                    {service.cta}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
              ))}
            </div>

            <p className="text-center body-md mt-12 max-w-3xl mx-auto text-[var(--text-muted)]">
              Every Rank and Cast engagement is delivered by the{' '}
              <Link to="/ops" className="text-[var(--accent-primary)] hover:underline">Marqi Ops</Link> team — the
              same execution standard agencies expect, without the overhead of building it in-house.
            </p>
          </div>
        </section>

        {/* SECTION 4 — WHICH CLIENTS NEED THIS */}
        <section className="py-24 bg-[var(--bg-secondary)]">
          <div className="container">
            <div className="text-center mb-16 max-w-3xl mx-auto">
              <h2 className="display-md text-[var(--text-primary)] mb-6">Every One of Your Clients Has a Discoverability Gap Right Now.</h2>
              <p className="body-lg">
                Some are invisible in AI answers. Some have never considered TV because the cost used to be
                prohibitive. Most have both gaps and don’t know it yet.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {verticals.map((v, i) => (
                <div key={v.name} className="feature-card hover-lift animate-fade-in" style={{ animationDelay: `${i * 0.1}s` }}>
                  <div className="w-12 h-12 rounded-full bg-[var(--accent-bg)] flex items-center justify-center text-[var(--accent-primary)] mb-6">{v.icon}</div>
                  <h3 className="h3 text-[var(--text-primary)] mb-3">{v.name}</h3>
                  <p className="body-md">{v.gap}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 5 — WHY NOW + CTA */}
        <section className="py-24" style={{ background: 'linear-gradient(135deg, var(--bg-secondary) 0%, var(--accent-bg) 100%)' }}>
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <h2 className="display-md text-[var(--text-primary)] mb-12 text-center">The Agencies Who Move First Own This Conversation.</h2>

              <div className="grid md:grid-cols-2 gap-10 mb-16">
                <div>
                  <div className="w-10 h-1 bg-[var(--accent-primary)] mb-5"></div>
                  <h3 className="h3 text-[var(--text-primary)] mb-3">The window is open, not permanent</h3>
                  <p className="body-md">
                    Right now, almost none of your competitors’ clients are optimised for AI search. Almost none
                    have considered streaming TV. That gap closes — probably within 18 months.
                  </p>
                </div>
                <div>
                  <div className="w-10 h-1 bg-[var(--accent-primary)] mb-5"></div>
                  <h3 className="h3 text-[var(--text-primary)] mb-3">Your clients will ask either way</h3>
                  <p className="body-md">
                    Whether or not your agency offers this, your clients’ customers are already asking AI and
                    watching streaming TV. The only question is whether your agency is the one who got them ready for it.
                  </p>
                </div>
              </div>

              <div className="text-center pt-12 border-t border-[var(--border-subtle)]">
                <h3 className="h1 text-[var(--text-primary)] mb-6">Ready to Make Your Clients Impossible to Miss?</h3>
                <p className="body-lg mb-12 max-w-2xl mx-auto">
                  Book a strategy session. We’ll look at your current client roster, show you exactly where the
                  discoverability gaps are, and what a Rank or Cast engagement would look like — before you commit
                  to anything.
                </p>
                <div className="flex flex-col sm:flex-row gap-6 justify-center">
                  <Link to="/contact" className="btn-primary glow-effect" data-testid="final-cta-primary">
                    Book a Free Strategy Session
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                  <Link to="/rank" className="btn-secondary" data-testid="final-cta-secondary">Explore Rank & Cast</Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Homepage;
